import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { contactApi } from "@/features/contact/contact.api";
import { subscribersApi } from "@/features/subscribers/subscribers.api";
import { useAuth } from "@/features/auth/AuthContext";
import type { PermissionResource } from "@/api/types";

export type BadgeKey = "contact" | "subscribers";

interface BadgeSource {
  key: BadgeKey;
  /** Route that clears the badge once visited. */
  path: string;
  resource: PermissionResource;
  /** Total number of relevant items right now. */
  fetchTotal: () => Promise<number>;
}

const SOURCES: BadgeSource[] = [
  {
    key: "contact",
    path: "/contact",
    resource: "CONTACT_SUBMISSIONS",
    // Only unanswered enquiries — one already marked read or replied is not
    // something the badge should keep nagging about.
    fetchTotal: async () => (await contactApi.list({ page: 1, limit: 1, status: "NEW" })).meta.total,
  },
  {
    key: "subscribers",
    path: "/subscribers",
    resource: "SUBSCRIBERS",
    fetchTotal: async () => (await subscribersApi.list({ page: 1, limit: 1 })).meta.total,
  },
];

const storageKey = (key: BadgeKey) => `admin:badge-seen:${key}`;

function readSeen(key: BadgeKey): number {
  const raw = localStorage.getItem(storageKey(key));
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

/**
 * Unseen counts for the sidebar.
 *
 * The badge is the number of items that have appeared since the section was
 * last opened, rather than a running total — a total would sit there forever
 * and stop meaning anything. Visiting the section stores the current total, so
 * the badge clears and only reappears when something new arrives.
 *
 * The high-water mark is per browser (localStorage), which is the right scope:
 * "have *I* looked at this yet" is a per-person question, and it needs no
 * server-side per-user state.
 */
export function useSidebarBadges(): Record<string, number> {
  const { hasPermission } = useAuth();
  const location = useLocation();
  const [seen, setSeen] = useState<Record<BadgeKey, number>>(() => ({
    contact: readSeen("contact"),
    subscribers: readSeen("subscribers"),
  }));

  const allowed = SOURCES.filter((source) => hasPermission(source.resource, "READ"));

  const totalsQuery = useQuery({
    queryKey: ["sidebar-badges", allowed.map((s) => s.key).join(",")],
    enabled: allowed.length > 0,
    // Cheap enough to re-check periodically so a new enquiry shows up without
    // a page reload, but not so often that it is chatty.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const entries = await Promise.all(
        allowed.map(async (source) => {
          try {
            return [source.key, await source.fetchTotal()] as const;
          } catch {
            // A failed count must never break the sidebar.
            return [source.key, 0] as const;
          }
        }),
      );
      return Object.fromEntries(entries) as Record<BadgeKey, number>;
    },
  });

  const totals = totalsQuery.data;

  const markSeen = useCallback((key: BadgeKey, total: number) => {
    localStorage.setItem(storageKey(key), String(total));
    setSeen((prev) => (prev[key] === total ? prev : { ...prev, [key]: total }));
  }, []);

  // Opening a section clears its badge. Runs on totals too, so arrivals while
  // the page is already open stay cleared rather than counting up behind you.
  useEffect(() => {
    if (!totals) return;
    for (const source of SOURCES) {
      if (location.pathname === source.path || location.pathname.startsWith(`${source.path}/`)) {
        markSeen(source.key, totals[source.key] ?? 0);
      }
    }
  }, [location.pathname, totals, markSeen]);

  const badges: Record<string, number> = {};
  if (totals) {
    for (const source of SOURCES) {
      // Deletions can push the total below the mark; never show a negative.
      badges[source.path] = Math.max(0, (totals[source.key] ?? 0) - seen[source.key]);
    }
  }
  return badges;
}
